import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from "@mui/material";
import { MdClose, MdPrint, MdCloudUpload, MdImage } from "react-icons/md";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import { getCustomerOwnQrTemplate } from "../../utils/api";
import useStore from "../../utils/store";

const computeSequentialSerial = (baseValue, index = 0) => {
  const str = String(baseValue || "1").trim();
  if (!str) return (index + 1).toString();
  const match = str.match(/^(.*?)(\d+)$/);
  if (match) {
    const prefix = match[1];
    const numStr = match[2];
    const startNum = parseInt(numStr, 10);
    const currentNum = startNum + index;
    const padded = String(currentNum).padStart(numStr.length, "0");
    return `${prefix}${padded}`;
  }
  return `${str}${index + 1}`;
};

const countLabelsInRange = (startStr, endStr) => {
  const s = String(startStr || "").trim();
  const e = String(endStr || "").trim();
  if (!s || !e) return 0;
  const sMatch = s.match(/^(.*?)(\d+)$/);
  const eMatch = e.match(/^(.*?)(\d+)$/);
  if (sMatch && eMatch) {
    if (sMatch[1] !== eMatch[1]) return 0;
    const sNum = parseInt(sMatch[2], 10);
    const eNum = parseInt(eMatch[2], 10);
    if (isNaN(sNum) || isNaN(eNum) || eNum < sNum) return 0;
    return eNum - sNum + 1;
  }
  const sNum = parseInt(s, 10);
  const eNum = parseInt(e, 10);
  if (!isNaN(sNum) && !isNaN(eNum)) {
    if (eNum < sNum) return 0;
    return eNum - sNum + 1;
  }
  return 0;
};

const CustomerQrPrintModal = ({ open, setOpen }) => {
  const user = useStore((state) => state.user);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Input Form, 2: Preview & Print

  // Customer form inputs
  const [formValues, setFormValues] = useState({});
  const [srStart, setSrStart] = useState(1);
  const [srEnd, setSrEnd] = useState(55);

  useEffect(() => {
    if (open) {
      setStep(1);
      fetchTemplate();
    }
  }, [open]);

  const fetchTemplate = async () => {
    setLoading(true);
    // First try user.customer.qrTemplate
    if (user?.customer?.qrTemplate && user.customer.qrTemplate.length) {
      applyTemplate(user.customer.qrTemplate, user.customer);
    } else {
      // Fetch fresh from backend
      const [success, data] = await getCustomerOwnQrTemplate(user?.token);
      if (success && data?.customer?.qrTemplate) {
        applyTemplate(data.customer.qrTemplate, data.customer);
      } else {
        setTemplate(null);
      }
    }
    setLoading(false);
  };

  const applyTemplate = (tmpl, cust) => {
    setTemplate(tmpl);
    const initialVals = {};
    (tmpl.fields || []).forEach((f) => {
      initialVals[f.id] = f.defaultValue || "";
    });
    // Fill from customer profile if empty
    if (!initialVals.name && cust?.name) initialVals.name = cust.name;
    if (!initialVals.agency && cust?.agency) initialVals.agency = cust.agency;
    if (!initialVals.fssai && cust?.fssai) initialVals.fssai = cust.fssai;
    if (!initialVals.center && cust?.location) initialVals.center = cust.location;

    setFormValues(initialVals);
    const defSr = (tmpl.fields || []).find(f => f.id === 'srNo')?.defaultValue || "1";
    setSrStart(defSr);
    setSrEnd(computeSequentialSerial(defSr, 54));
  };

  const handleInputChange = (id, val) => {
    setFormValues((prev) => ({ ...prev, [id]: val }));
  };

  const handleProceedToPreview = () => {
    const total = countLabelsInRange(srStart, srEnd);
    if (!srStart || !srEnd || total < 1) {
      toast.error("Please enter a valid Sr. No range (Start <= End)");
      return;
    }
    if (total > 500) {
      toast.error("Maximum 500 labels can be generated in one batch.");
      return;
    }
    setStep(2);
  };

  const handlePrint = () => {
    window.print();
  };

  // Generate labels array for preview
  const generateLabels = () => {
    if (!template) return [];
    const labels = [];
    const total = countLabelsInRange(srStart, srEnd);
    for (let i = 0; i < total; i++) {
      const currentSr = computeSequentialSerial(srStart, i);
      const fieldLines = [];
      const qrLines = [];

      (template.fields || []).forEach((f) => {
        let val = formValues[f.id] !== undefined ? formValues[f.id] : f.defaultValue || "";
        if (f.id === "srNo") {
          val = currentSr;
        }

        const formatted = f.printPrefix ? `${f.label}: ${val}` : val;
        fieldLines.push({
          id: f.id,
          label: f.label,
          text: formatted,
          val: val,
          type: f.type || "text",
          imageWidth: f.imageWidth || 40,
          imageHeight: f.imageHeight || 40,
          printOnLabel: f.printOnLabel !== false,
        });
        if (f.includeInQr !== false) {
          if (f.type === "image") {
            if (val && !val.startsWith("data:")) {
              qrLines.push(`${f.label}: ${val}`);
            }
          } else {
            if (f.id === "srNo" || f.printPrefix === false) {
              qrLines.push(val);
            } else {
              qrLines.push(`${f.label}: ${val}`);
            }
          }
        }
      });

      labels.push({
        srNo: currentSr,
        fields: fieldLines,
        qrText: qrLines.join("\n"),
      });
    }

    return labels;
  };

  const labels = step === 2 ? generateLabels() : [];
  const lengthMm = template?.length || 100;
  const heightMm = template?.height || 50;
  const upCount = template?.up || 1;

  // Filter fields that are enabled for customer
  const customerFields = (template?.fields || []).filter(
    (f) => f.showToCustomer && f.id !== "srNo"
  );

  return (
    <>
      {/* Dynamic Print CSS for Thermal / Standard Page Printing */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #printable-qr-labels, #printable-qr-labels * {
            visibility: visible !important;
          }
          #printable-qr-labels {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .no-print {
            display: none !important;
          }
          @page {
            size: ${lengthMm * upCount}mm ${heightMm}mm;
            margin: 0mm;
          }
        }
      `}</style>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle className="flex justify-between items-center bg-emerald-800 text-white py-3 px-5 no-print">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🖨️</span>
            <div>
              <span className="font-bold text-base md:text-lg">QR Code Label Print</span>
              <span className="block text-xs text-emerald-200">
                {template
                  ? `Size: ${lengthMm}mm × ${heightMm}mm (${upCount} Up)`
                  : "Label Setup"}
              </span>
            </div>
          </div>
          <IconButton onClick={() => setOpen(false)} size="small" className="text-white">
            <MdClose className="text-white text-xl" />
          </IconButton>
        </DialogTitle>

        <DialogContent className="p-6">
          {loading ? (
            <div className="py-12 text-center text-gray-500">Loading template...</div>
          ) : !template ? (
            <div className="py-12 text-center">
              <div className="text-4xl mb-3">⚙️</div>
              <h2 className="text-lg font-bold text-gray-800">No QR Template Configured</h2>
              <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                Admin has not configured a QR label template for your account yet. Please contact the administrator to set up your label dimensions and fields.
              </p>
            </div>
          ) : step === 1 ? (
            /* Step 1: Customer Details Form */
            <div className="flex flex-col gap-6 max-w-xl mx-auto py-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
                📝 Please review and update your label details below, then enter your <strong>Sr. No range</strong> (Start & End) to preview and print.
              </div>

              {/* Dynamic Customer Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customerFields.map((f) => {
                  if (f.type === "image") {
                    return (
                      <div key={f.id} className="flex flex-col gap-1 border border-slate-200 p-2.5 rounded-lg bg-slate-50">
                        <label className="text-xs font-bold text-gray-700 flex items-center justify-between">
                          <span>🖼️ {f.label}</span>
                          {formValues[f.id] && <span className="text-[10px] text-emerald-600 font-bold">Image Set</span>}
                        </label>
                        <div className="flex items-center gap-2">
                          {formValues[f.id] ? (
                            <img src={formValues[f.id]} alt={f.label} className="w-10 h-10 object-contain rounded border bg-white p-0.5" />
                          ) : (
                            <div className="w-10 h-10 rounded border border-dashed flex items-center justify-center text-[9px] text-gray-400 bg-white">
                              No Img
                            </div>
                          )}
                          <label className="text-[11px] font-bold bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-2.5 py-1 rounded cursor-pointer transition">
                            Upload
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (ev) => handleInputChange(f.id, ev.target.result);
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <TextField
                      key={f.id}
                      label={f.label}
                      type={f.type === "number" ? "number" : "text"}
                      value={formValues[f.id] || ""}
                      onChange={(e) => handleInputChange(f.id, e.target.value)}
                      fullWidth
                      size="small"
                      variant="outlined"
                    />
                  );
                })}
              </div>

              {/* Sr. No Range */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5">
                  <span>🔢</span> Serial Number Range (Sr. No)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <TextField
                    label="Start Serial (e.g. 1 or Abc123)"
                    type="text"
                    value={srStart}
                    onChange={(e) => setSrStart(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                  <TextField
                    label="End Serial (e.g. 55 or Abc177)"
                    type="text"
                    value={srEnd}
                    onChange={(e) => setSrEnd(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Total labels to be printed:{" "}
                  <strong>{Math.max(0, countLabelsInRange(srStart, srEnd))}</strong>
                </div>
              </div>
            </div>
          ) : (
            /* Step 2: Multi-Label Preview */
            <div className="flex flex-col gap-4 py-2">
              <div className="flex justify-between items-center text-xs text-gray-600 bg-gray-50 p-2.5 rounded border no-print">
                <span>
                  Total Labels: <strong>{labels.length}</strong> (Sr. {srStart} to {srEnd})
                </span>
                <span>
                  Layout: <strong>{upCount} Up</strong> | Size: <strong>{lengthMm}mm × {heightMm}mm</strong>
                </span>
              </div>

              {/* Printable Labels Container */}
              <div
                id="printable-qr-labels"
                className="p-4 bg-slate-100 rounded-xl overflow-y-auto max-h-[60vh] flex flex-col items-center gap-3"
              >
                <div
                  className="grid gap-3 w-full justify-center"
                  style={{
                    gridTemplateColumns: `repeat(${upCount}, minmax(0, 1fr))`,
                    maxWidth: `${upCount * 330}px`,
                  }}
                >
                  {labels.map((lbl) => (
                    <div
                      key={lbl.srNo}
                      className="border-2 border-black p-2.5 flex flex-row items-center justify-between gap-2 bg-white rounded-none page-break-inside-avoid"
                      style={{
                        width: `${Math.min(320, lengthMm * 2.8)}px`,
                        minHeight: `${Math.min(220, heightMm * 2.8)}px`,
                        boxSizing: "border-box",
                      }}
                    >
                      {/* Left Text Fields */}
                      <div className="flex flex-col justify-center flex-1 text-[11px] leading-tight space-y-1 font-mono text-black overflow-hidden">
                        {lbl.fields.map((f) => {
                          if (f.printOnLabel === false) return null;
                          if (f.type === "image") {
                            return (
                              <div key={f.id} style={{ width: `${f.imageWidth || 40}px`, height: `${f.imageHeight || 40}px` }} className="my-0.5">
                                {f.val ? <img src={f.val} alt={f.label} className="w-full h-full object-contain" /> : null}
                              </div>
                            );
                          }
                          return (
                            <div key={f.id} className="truncate">
                              {f.text}
                            </div>
                          );
                        })}
                      </div>

                      {/* Right Dynamic QR Code (only if printQr !== false) */}
                      {template?.printQr !== false && (
                        <div className="shrink-0 flex flex-col items-center justify-center bg-white">
                          <QRCodeSVG
                            value={lbl.qrText}
                            size={template?.qrDimension ? Math.min(130, Math.max(40, template.qrDimension)) : Math.min(90, Math.max(50, heightMm * 1.5))}
                            level="M"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between no-print">
          <div>
            {step === 2 && (
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                size="small"
                className="font-bold"
              >
                Back to Edit
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step === 1 && template ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleProceedToPreview}
                size="small"
                className="font-bold"
              >
                Preview Labels
              </Button>
            ) : step === 2 ? (
              <Button
                variant="contained"
                color="success"
                onClick={handlePrint}
                startIcon={<MdPrint />}
                size="medium"
                className="font-bold bg-emerald-600 hover:bg-emerald-700"
              >
                Print Labels ({labels.length})
              </Button>
            ) : null}
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerQrPrintModal;
